/**
 * LA 2028 Olympics Schedule Viewer
 * Main application logic
 */

// ============================================
// STATE MANAGEMENT
// ============================================

const ScheduleApp = {
  // Data
  data: {
    sessions: [],
    filteredSessions: [],
    sportColors: {},
    venues: [],
    mySchedule: [],
  },

  // UI State
  state: {
    activeView: 'calendar',
    currentWeekStart: null, // Date object for Monday of current week
    filters: {
      dateRange: '',
      sports: [],
      venues: [],
      searchQuery: '',
    },
    loading: true,
    error: null,
    currentPage: 1,
    expandedVenues: new Set(), // Track which venues are expanded
    venuesSortBy: 'sessions', // 'sessions' or 'name'
    isInitializing: false, // Flag to prevent duplicate URL updates during init
    currentMobileDayIndex: 0, // Track current day in mobile calendar (0=Monday to 6=Sunday)
  },

  // Initialize app
  async init() {
    console.log('Initializing Schedule App...');

    try {
      this.state.isInitializing = true;

      await this.loadData();
      this.loadMySchedule();

      // Parse and apply URL parameters before UI initialization
      const urlParams = this.parseUrlParams();
      this.applyUrlParams(urlParams);

      this.initializeUI();
      this.updateFilterUI();
      this.attachEventListeners();
      this.initMobileFeatures(); // Initialize mobile-specific features
      this.initSwipeGestures(); // Initialize swipe gestures for mobile
      this.renderCurrentView();

      // Listen for browser back/forward buttons
      window.addEventListener('popstate', (e) => this.handlePopState(e));

      this.state.isInitializing = false;
    } catch (error) {
      console.error('Failed to initialize app:', error);
      this.showError(error.message);
      this.state.isInitializing = false;
    }
  },

  // Load all JSON data
  async loadData() {
    try {
      // Get asset URLs from config
      const scheduleDataUrl = window.SCHEDULE_CONFIG?.scheduleDataUrl;
      const sportColorsUrl = window.SCHEDULE_CONFIG?.sportColorsUrl;
      const venuesDataUrl = window.SCHEDULE_CONFIG?.venuesDataUrl;

      if (!scheduleDataUrl || !sportColorsUrl || !venuesDataUrl) {
        throw new Error('Asset URLs not configured properly');
      }

      // Load all data files in parallel
      const [scheduleData, sportColors, venues] = await Promise.all([
        fetch(scheduleDataUrl).then(r => r.json()),
        fetch(sportColorsUrl).then(r => r.json()),
        fetch(venuesDataUrl).then(r => r.json()),
      ]);

      this.data.sessions = scheduleData.sessions;
      this.data.filteredSessions = [...scheduleData.sessions];
      this.data.sportColors = sportColors;
      this.data.venues = venues;

      console.log(`✓ Loaded ${this.data.sessions.length} sessions`);
      this.state.loading = false;

    } catch (error) {
      console.error('Failed to load data:', error);
      throw new Error('Failed to load schedule data. Please refresh the page.');
    }
  },

  // Load "My Schedule" from localStorage
  loadMySchedule() {
    try {
      const saved = localStorage.getItem('la2028_my_schedule');
      if (saved) {
        this.data.mySchedule = JSON.parse(saved);
        console.log(`✓ Loaded ${this.data.mySchedule.length} saved sessions`);
      }
    } catch (error) {
      console.error('Failed to load My Schedule:', error);
    }
  },

  // Save "My Schedule" to localStorage
  saveMySchedule() {
    try {
      localStorage.setItem('la2028_my_schedule', JSON.stringify(this.data.mySchedule));
    } catch (error) {
      console.error('Failed to save My Schedule:', error);
    }
  },

  // Show error message
  showError(message) {
    const content = document.getElementById('schedule-content');
    if (content) {
      content.innerHTML = `
        <div class="schedule-error">
          <p>⚠️ ${message}</p>
          <button onclick="location.reload()">Reload Page</button>
        </div>
      `;
    }
  },

  // ============================================
  // TASK 14: URL PARAMETER HANDLING
  // ============================================

  // Parse URL parameters into object
  parseUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const result = {};

    // View parameter
    if (params.has('view')) {
      const view = params.get('view');
      if (['calendar', 'list', 'venues'].includes(view)) {
        result.view = view;
      }
    }

    // Sports (single or multiple)
    if (params.has('sport')) {
      result.sports = [decodeURIComponent(params.get('sport'))];
    } else if (params.has('sports')) {
      result.sports = params.get('sports')
        .split(',')
        .map(s => decodeURIComponent(s.trim()))
        .filter(s => s.length > 0);
    }

    // Venues (single or multiple)
    if (params.has('venue')) {
      result.venues = [decodeURIComponent(params.get('venue'))];
    } else if (params.has('venues')) {
      result.venues = params.get('venues')
        .split(',')
        .map(v => decodeURIComponent(v.trim()))
        .filter(v => v.length > 0);
    }

    // Date range
    if (params.has('dateRange')) {
      const dateRange = params.get('dateRange');
      if (['today', 'tomorrow', 'this-week'].includes(dateRange)) {
        result.dateRange = dateRange;
      }
    }

    // Specific date (YYYY-MM-DD)
    if (params.has('date')) {
      const dateStr = params.get('date');
      // Validate date format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        result.date = dateStr;
      }
    }

    // Search query
    if (params.has('search')) {
      const search = decodeURIComponent(params.get('search')).trim();
      if (search.length > 0) {
        result.search = search;
      }
    }

    // Session code (to open detail)
    if (params.has('session')) {
      const session = params.get('session').trim();
      if (session.length > 0) {
        result.session = session;
      }
    }

    // Calendar week start
    if (params.has('weekStart')) {
      const weekStr = params.get('weekStart');
      // Validate date format
      if (/^\d{4}-\d{2}-\d{2}$/.test(weekStr)) {
        try {
          const date = new Date(weekStr + 'T00:00:00');
          if (!isNaN(date.getTime())) {
            result.weekStart = date;
          }
        } catch (e) {
          // Ignore invalid dates
        }
      }
    }

    // Venues sort
    if (params.has('venueSort')) {
      const sort = params.get('venueSort');
      if (['sessions', 'name'].includes(sort)) {
        result.venueSort = sort;
      }
    }

    // Expanded venues
    if (params.has('expanded')) {
      result.expanded = params.get('expanded')
        .split(',')
        .map(v => decodeURIComponent(v.trim()))
        .filter(v => v.length > 0);
    }

    return result;
  },

  // Apply URL parameters to app state
  applyUrlParams(params) {
    // Set view
    if (params.view) {
      this.state.activeView = params.view;
    }

    // Set filters
    if (params.sports) {
      // Validate against actual sports in data
      const validSports = params.sports.filter(sport =>
        this.data.sessions.some(s => s.sport === sport)
      );
      if (validSports.length > 0) {
        this.state.filters.sports = validSports;
      }
    }

    if (params.venues) {
      // Validate against actual venues in data
      const validVenues = params.venues.filter(venue =>
        this.data.sessions.some(s => s.venue === venue)
      );
      if (validVenues.length > 0) {
        this.state.filters.venues = validVenues;
      }
    }

    if (params.dateRange) {
      this.state.filters.dateRange = params.dateRange;
    }

    if (params.date) {
      // Custom date filter - set as dateRange temporarily
      this.state.filters.dateRange = params.date;
    }

    if (params.search) {
      this.state.filters.searchQuery = params.search;
    }

    // Set calendar week
    if (params.weekStart && params.view === 'calendar') {
      this.state.currentWeekStart = params.weekStart;
    }

    // Set venues sort
    if (params.venueSort && params.view === 'venues') {
      this.state.venuesSortBy = params.venueSort;
    }

    // Set expanded venues
    if (params.expanded && params.view === 'venues') {
      this.state.expandedVenues = new Set(params.expanded);
    }

    // Apply filters if any were set (only if not initializing)
    if (!this.state.isInitializing) {
      if (params.sports || params.venues || params.dateRange || params.date || params.search) {
        this.applyFilters();
      }
    } else {
      // During initialization, just update filtered sessions without rendering
      if (params.sports || params.venues || params.dateRange || params.date || params.search) {
        let filtered = [...this.data.sessions];

        if (this.state.filters.dateRange) {
          filtered = this.filterByDateRange(filtered, this.state.filters.dateRange);
        }

        if (this.state.filters.sports.length > 0) {
          filtered = filtered.filter(session =>
            this.state.filters.sports.includes(session.sport)
          );
        }

        if (this.state.filters.venues.length > 0) {
          filtered = filtered.filter(session =>
            this.state.filters.venues.includes(session.venue)
          );
        }

        if (this.state.filters.searchQuery) {
          const query = this.state.filters.searchQuery;
          filtered = filtered.filter(session => {
            return (
              session.code.toLowerCase().includes(query) ||
              session.sport.toLowerCase().includes(query) ||
              session.event.toLowerCase().includes(query) ||
              session.venue.toLowerCase().includes(query)
            );
          });
        }

        this.data.filteredSessions = filtered;
      }
    }

    // Open session detail if specified (will be done after render)
    if (params.session) {
      this.pendingSessionDetail = params.session;
    }
  },

  // Update filter UI to reflect current state
  updateFilterUI() {
    // Update date filter dropdown
    const dateFilter = document.getElementById('date-filter');
    if (dateFilter && this.state.filters.dateRange) {
      dateFilter.value = this.state.filters.dateRange;
    }

    // Update sport checkboxes
    if (this.state.filters.sports.length > 0) {
      this.state.filters.sports.forEach(sport => {
        const checkbox = document.querySelector(`#sport-filter-options input[value="${sport}"]`);
        if (checkbox) checkbox.checked = true;
      });
      this.updateFilterButton('sport-filter-button', this.state.filters.sports.length, 'Sport');
    }

    // Update venue checkboxes
    if (this.state.filters.venues.length > 0) {
      this.state.filters.venues.forEach(venue => {
        const checkbox = document.querySelector(`#venue-filter-options input[value="${venue}"]`);
        if (checkbox) checkbox.checked = true;
      });
      this.updateFilterButton('venue-filter-button', this.state.filters.venues.length, 'Venue');
    }

    // Update search input
    const searchInput = document.getElementById('search-filter');
    if (searchInput && this.state.filters.searchQuery) {
      searchInput.value = this.state.filters.searchQuery;
    }

    // Update active view tab
    document.querySelectorAll('.schedule-tab').forEach(tab => {
      tab.classList.toggle('active', tab.dataset.view === this.state.activeView);
    });
  },

  // Convert current state to URL parameters
  getStateAsUrlParams() {
    const params = {};

    // View (only if not default 'list')
    if (this.state.activeView !== 'list') {
      params.view = this.state.activeView;
    }

    // Filters
    if (this.state.filters.sports.length > 0) {
      if (this.state.filters.sports.length === 1) {
        params.sport = encodeURIComponent(this.state.filters.sports[0]);
      } else {
        params.sports = this.state.filters.sports
          .map(s => encodeURIComponent(s))
          .join(',');
      }
    }

    if (this.state.filters.venues.length > 0) {
      if (this.state.filters.venues.length === 1) {
        params.venue = encodeURIComponent(this.state.filters.venues[0]);
      } else {
        params.venues = this.state.filters.venues
          .map(v => encodeURIComponent(v))
          .join(',');
      }
    }

    if (this.state.filters.dateRange) {
      params.dateRange = this.state.filters.dateRange;
    }

    if (this.state.filters.searchQuery) {
      params.search = encodeURIComponent(this.state.filters.searchQuery);
    }

    // Calendar-specific
    if (this.state.activeView === 'calendar' && this.state.currentWeekStart) {
      params.weekStart = this.formatDateString(this.state.currentWeekStart);
    }

    // Venues-specific
    if (this.state.activeView === 'venues') {
      if (this.state.venuesSortBy !== 'sessions') {
        params.venueSort = this.state.venuesSortBy;
      }

      if (this.state.expandedVenues.size > 0) {
        params.expanded = Array.from(this.state.expandedVenues)
          .map(v => encodeURIComponent(v))
          .join(',');
      }
    }

    return params;
  },

  // Update URL without reloading page
  updateUrl() {
    // Don't update URL during initialization to avoid duplicate history entries
    if (this.state.isInitializing) {
      return;
    }

    const params = this.getStateAsUrlParams();
    const urlParams = new URLSearchParams(params);
    const newUrl = urlParams.toString()
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;

    // Only update if URL changed
    const currentUrl = window.location.pathname + window.location.search;
    if (newUrl !== currentUrl) {
      history.pushState({ scheduleState: this.state }, '', newUrl);
    }
  },

  // Handle browser back/forward buttons
  handlePopState(event) {
    const params = this.parseUrlParams();
    this.applyUrlParams(params);
    this.updateFilterUI();
    this.renderCurrentView();
  },

  // ============================================
  // TASK 7: FILTER INITIALIZATION AND UI
  // ============================================

  // Initialize UI elements
  initializeUI() {
    this.populateSportFilter();
    this.populateVenueFilter();
    this.updateSessionCounter();
  },

  // Populate sport filter options
  populateSportFilter() {
    const container = document.getElementById('sport-filter-options');
    if (!container) return;

    // Get unique sports with counts
    const sportCounts = {};
    this.data.sessions.forEach(session => {
      sportCounts[session.sport] = (sportCounts[session.sport] || 0) + 1;
    });

    // Sort alphabetically
    const sports = Object.keys(sportCounts).sort();

    // Create checkboxes
    container.innerHTML = sports.map(sport => `
      <label class="filter-option">
        <input type="checkbox" name="sport" value="${sport}" data-count="${sportCounts[sport]}">
        <span class="filter-option-label">${sport}</span>
        <span class="filter-option-count">${sportCounts[sport]}</span>
      </label>
    `).join('');
  },

  // Populate venue filter options
  populateVenueFilter() {
    const container = document.getElementById('venue-filter-options');
    if (!container) return;

    // Get unique venues with counts
    const venueCounts = {};
    this.data.sessions.forEach(session => {
      venueCounts[session.venue] = (venueCounts[session.venue] || 0) + 1;
    });

    // Sort by count (descending)
    const venues = Object.entries(venueCounts)
      .sort(([, a], [, b]) => b - a)
      .map(([venue]) => venue);

    // Create checkboxes
    container.innerHTML = venues.map(venue => `
      <label class="filter-option">
        <input type="checkbox" name="venue" value="${venue}" data-count="${venueCounts[venue]}">
        <span class="filter-option-label">${venue}</span>
        <span class="filter-option-count">${venueCounts[venue]}</span>
      </label>
    `).join('');
  },

  // Update session counter
  updateSessionCounter() {
    const counter = document.getElementById('session-count');
    if (!counter) return;

    const total = this.data.sessions.length;
    const showing = this.data.filteredSessions.length;

    if (showing === total) {
      counter.textContent = `Showing all ${total} sessions`;
    } else {
      counter.textContent = `Showing ${showing} of ${total} sessions`;
    }
  },

  // ============================================
  // TASK 8: FILTER EVENT LISTENERS
  // ============================================

  // Attach all event listeners
  attachEventListeners() {
    // Tab switching
    document.querySelectorAll('.schedule-tab').forEach(tab => {
      tab.addEventListener('click', (e) => this.handleTabClick(e));
    });

    // Date filter
    const dateFilter = document.getElementById('date-filter');
    if (dateFilter) {
      dateFilter.addEventListener('change', (e) => {
        this.state.filters.dateRange = e.target.value;
        this.applyFilters();
      });
    }

    // Sport filter dropdown toggle
    const sportButton = document.getElementById('sport-filter-button');
    const sportDropdown = document.getElementById('sport-filter-dropdown');
    if (sportButton && sportDropdown) {
      sportButton.addEventListener('click', (e) => {
        e.stopPropagation();
        sportDropdown.style.display = sportDropdown.style.display === 'none' ? 'block' : 'none';
        sportButton.classList.toggle('open');
      });
    }

    // Venue filter dropdown toggle
    const venueButton = document.getElementById('venue-filter-button');
    const venueDropdown = document.getElementById('venue-filter-dropdown');
    if (venueButton && venueDropdown) {
      venueButton.addEventListener('click', (e) => {
        e.stopPropagation();
        venueDropdown.style.display = venueDropdown.style.display === 'none' ? 'block' : 'none';
        venueButton.classList.toggle('open');
      });
    }

    // Close dropdowns when clicking outside
    document.addEventListener('click', () => {
      if (sportDropdown) {
        sportDropdown.style.display = 'none';
        sportButton?.classList.remove('open');
      }
      if (venueDropdown) {
        venueDropdown.style.display = 'none';
        venueButton?.classList.remove('open');
      }
    });

    // Sport filter checkboxes
    document.querySelectorAll('#sport-filter-options input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleSportFilterChange());
    });

    // Venue filter checkboxes
    document.querySelectorAll('#venue-filter-options input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleVenueFilterChange());
    });

    // Search filter
    const searchInput = document.getElementById('search-filter');
    if (searchInput) {
      let searchTimeout;
      searchInput.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          this.state.filters.searchQuery = e.target.value.toLowerCase();
          this.applyFilters();
        }, 300); // Debounce 300ms
      });
    }

    // Clear filters button
    const clearButton = document.getElementById('clear-filters');
    if (clearButton) {
      clearButton.addEventListener('click', () => this.clearAllFilters());
    }

    // Sport filter search
    const sportSearch = document.getElementById('sport-search');
    if (sportSearch) {
      sportSearch.addEventListener('input', (e) => this.filterDropdownOptions(e.target, 'sport-filter-options'));
    }

    // Venue filter search
    const venueSearch = document.getElementById('venue-search');
    if (venueSearch) {
      venueSearch.addEventListener('input', (e) => this.filterDropdownOptions(e.target, 'venue-filter-options'));
    }
  },

  // Handle tab click
  handleTabClick(event) {
    const tab = event.currentTarget;
    const view = tab.dataset.view;

    // Update tab states
    document.querySelectorAll('.schedule-tab').forEach(t => t.classList.remove('active'));
    tab.classList.add('active');

    // Update active view
    this.state.activeView = view;
    this.renderCurrentView();

    // Update URL to reflect view change
    this.updateUrl();
  },

  // Handle sport filter change
  handleSportFilterChange() {
    const checked = Array.from(document.querySelectorAll('#sport-filter-options input:checked'))
      .map(input => input.value);

    this.state.filters.sports = checked;
    this.updateFilterButton('sport-filter-button', checked.length, 'Sport');
    this.applyFilters();
  },

  // Handle venue filter change
  handleVenueFilterChange() {
    const checked = Array.from(document.querySelectorAll('#venue-filter-options input:checked'))
      .map(input => input.value);

    this.state.filters.venues = checked;
    this.updateFilterButton('venue-filter-button', checked.length, 'Venue');
    this.applyFilters();
  },

  // Update filter button text
  updateFilterButton(buttonId, count, label) {
    const button = document.getElementById(buttonId);
    if (!button) return;

    const span = button.querySelector('span');
    if (count === 0) {
      span.textContent = `All ${label}s`;
    } else if (count === 1) {
      span.textContent = `1 ${label}`;
    } else {
      span.textContent = `${count} ${label}s`;
    }
  },

  // Filter dropdown options based on search
  filterDropdownOptions(searchInput, containerId) {
    const query = searchInput.value.toLowerCase();
    const container = document.getElementById(containerId);
    if (!container) return;

    container.querySelectorAll('.filter-option').forEach(option => {
      const label = option.querySelector('.filter-option-label').textContent.toLowerCase();
      option.style.display = label.includes(query) ? 'flex' : 'none';
    });
  },

  // Clear all filters
  clearAllFilters() {
    // Reset filter state
    this.state.filters = {
      dateRange: '',
      sports: [],
      venues: [],
      searchQuery: '',
    };

    // Reset UI
    document.getElementById('date-filter').value = '';
    document.getElementById('search-filter').value = '';

    document.querySelectorAll('#sport-filter-options input[type="checkbox"]').forEach(cb => cb.checked = false);
    document.querySelectorAll('#venue-filter-options input[type="checkbox"]').forEach(cb => cb.checked = false);

    this.updateFilterButton('sport-filter-button', 0, 'Sport');
    this.updateFilterButton('venue-filter-button', 0, 'Venue');

    this.applyFilters();
  },

  // ============================================
  // TASK 9: FILTER APPLICATION LOGIC
  // ============================================

  // Apply all filters to sessions
  applyFilters() {
    let filtered = [...this.data.sessions];

    // Date range filter
    if (this.state.filters.dateRange) {
      filtered = this.filterByDateRange(filtered, this.state.filters.dateRange);
    }

    // Sport filter
    if (this.state.filters.sports.length > 0) {
      filtered = filtered.filter(session =>
        this.state.filters.sports.includes(session.sport)
      );
    }

    // Venue filter
    if (this.state.filters.venues.length > 0) {
      filtered = filtered.filter(session =>
        this.state.filters.venues.includes(session.venue)
      );
    }

    // Search filter
    if (this.state.filters.searchQuery) {
      const query = this.state.filters.searchQuery;
      filtered = filtered.filter(session => {
        return (
          session.code.toLowerCase().includes(query) ||
          session.sport.toLowerCase().includes(query) ||
          session.event.toLowerCase().includes(query) ||
          session.venue.toLowerCase().includes(query)
        );
      });
    }

    this.data.filteredSessions = filtered;
    this.updateSessionCounter();
    this.renderActiveFilters();
    this.renderCurrentView();

    // Update mobile filter badge
    this.updateMobileFilterBadge();

    // Update URL to reflect filters
    this.updateUrl();
  },

  // Filter by date range
  filterByDateRange(sessions, range) {
    const today = new Date().toISOString().split('T')[0];
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];

    switch (range) {
      case 'today':
        return sessions.filter(s => s.date === today);

      case 'tomorrow':
        return sessions.filter(s => s.date === tomorrow);

      case 'this-week': {
        const weekFromNow = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
        return sessions.filter(s => s.date >= today && s.date <= weekFromNow);
      }

      default:
        return sessions;
    }
  },

  // Render active filter pills
  renderActiveFilters() {
    const container = document.getElementById('active-filters');
    if (!container) return;

    const pills = [];

    // Date filter
    if (this.state.filters.dateRange) {
      const labels = {
        'today': 'Today',
        'tomorrow': 'Tomorrow',
        'this-week': 'This Week'
      };
      pills.push(this.createFilterPill('date', labels[this.state.filters.dateRange]));
    }

    // Sport filters
    this.state.filters.sports.forEach(sport => {
      pills.push(this.createFilterPill('sport', sport));
    });

    // Venue filters
    this.state.filters.venues.forEach(venue => {
      pills.push(this.createFilterPill('venue', venue));
    });

    // Search filter
    if (this.state.filters.searchQuery) {
      pills.push(this.createFilterPill('search', `"${this.state.filters.searchQuery}"`));
    }

    container.innerHTML = pills.join('');

    // Attach remove listeners
    container.querySelectorAll('.active-filter-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const type = e.target.dataset.type;
        const value = e.target.dataset.value;
        this.removeFilter(type, value);
      });
    });
  },

  // Create filter pill HTML
  createFilterPill(type, value) {
    return `
      <div class="active-filter-pill">
        <span>${value}</span>
        <button class="active-filter-remove" data-type="${type}" data-value="${value}" aria-label="Remove filter">×</button>
      </div>
    `;
  },

  // Remove specific filter
  removeFilter(type, value) {
    switch (type) {
      case 'date':
        this.state.filters.dateRange = '';
        document.getElementById('date-filter').value = '';
        break;

      case 'sport': {
        this.state.filters.sports = this.state.filters.sports.filter(s => s !== value);
        const checkbox = document.querySelector(`#sport-filter-options input[value="${value}"]`);
        if (checkbox) checkbox.checked = false;
        this.updateFilterButton('sport-filter-button', this.state.filters.sports.length, 'Sport');
        break;
      }

      case 'venue': {
        this.state.filters.venues = this.state.filters.venues.filter(v => v !== value);
        const checkbox = document.querySelector(`#venue-filter-options input[value="${value}"]`);
        if (checkbox) checkbox.checked = false;
        this.updateFilterButton('venue-filter-button', this.state.filters.venues.length, 'Venue');
        break;
      }

      case 'search':
        this.state.filters.searchQuery = '';
        document.getElementById('search-filter').value = '';
        break;
    }

    this.applyFilters();
  },

  // ============================================
  // TASK 10: LIST VIEW RENDERING
  // ============================================

  // Render current view based on active tab
  renderCurrentView() {
    switch (this.state.activeView) {
      case 'calendar':
        this.renderCalendarView();
        break;
      case 'list':
        this.renderListView();
        break;
      case 'venues':
        this.renderVenuesView();
        break;
    }

    // Open session detail if pending from URL parameter
    if (this.pendingSessionDetail) {
      const code = this.pendingSessionDetail;
      this.pendingSessionDetail = null;
      setTimeout(() => this.openSessionDetail(code), 100);
    }
  },

  // Render list view with session cards
  renderListView() {
    const content = document.getElementById('schedule-content');
    if (!content) return;

    // Reset page counter when rendering fresh view
    this.state.currentPage = 1;

    // Show empty state if no sessions match filters
    if (this.data.filteredSessions.length === 0) {
      content.innerHTML = `
        <div class="schedule-empty">
          <p>No sessions match your filters</p>
          <button onclick="ScheduleApp.clearAllFilters()">Clear Filters</button>
        </div>
      `;
      return;
    }

    // Create card grid with first 50 sessions
    const cards = this.data.filteredSessions
      .slice(0, 50)
      .map(session => this.createSessionCard(session))
      .join('');

    content.innerHTML = `
      <div class="session-grid">
        ${cards}
      </div>
      ${this.data.filteredSessions.length > 50 ? `
        <div class="load-more-container">
          <button id="load-more-btn" class="load-more-button">
            Load More Sessions (${this.data.filteredSessions.length - 50} remaining)
          </button>
        </div>
      ` : ''}
    `;

    // Attach load more listener
    const loadMoreBtn = document.getElementById('load-more-btn');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', () => this.loadMoreSessions());
    }

    // Attach session card listeners
    this.attachSessionCardListeners();
  },

  // Create session card HTML
  createSessionCard(session) {
    const isFavorite = this.data.mySchedule.includes(session.code);
    const sportColor = this.data.sportColors[session.sport] || '#999';
    const sportIcon = this.getSportIcon(session.sport);

    // Format date
    const date = new Date(session.date + 'T00:00:00');
    const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

    return `
      <article class="session-card ${isFavorite ? 'is-favorite' : ''}" data-session-code="${session.code}" data-sport="${session.sport}" data-venue="${session.venue}" style="border-left-color: ${sportColor}">
        <div class="session-card-header">
          <span class="session-code">${session.code}</span>
          <button class="session-favorite ${isFavorite ? 'active' : ''}" data-code="${session.code}" aria-label="Add to My Schedule">
            <span class="favorite-icon"></span>
          </button>
        </div>
        <div class="session-card-sport">
          <span class="sport-icon">${sportIcon}</span>
          <span class="sport-name">${session.sport}</span>
        </div>
        <div class="session-card-time">
          <span class="session-date">${dateStr}</span>
          <span class="session-time">${session.time}</span>
        </div>
        <div class="session-card-event">${session.event}</div>
        <div class="session-card-venue">
          <span class="venue-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
          </span>
          <span class="venue-name">${session.venue}</span>
        </div>
        <button class="session-card-cta" data-code="${session.code}">View Details</button>
      </article>
    `;
  },

  // Get sport icon (simple SVG)
  getSportIcon(sport) {
    const color = this.data.sportColors[sport] || '#999';
    const iconSvg = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="8" fill="${color}"/>
    </svg>`;
    return iconSvg;
  },

  // Attach event listeners to session cards
  attachSessionCardListeners() {
    // View details buttons
    document.querySelectorAll('.session-card-cta').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const code = btn.dataset.code;
        this.openSessionDetail(code);
      });
    });

    // Favorite buttons
    document.querySelectorAll('.session-favorite').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const code = btn.dataset.code;
        this.toggleFavorite(code);
      });
    });

    // Card click
    document.querySelectorAll('.session-card').forEach(card => {
      card.addEventListener('click', () => {
        const code = card.dataset.sessionCode;
        this.openSessionDetail(code);
      });
    });
  },

  // Toggle favorite status
  toggleFavorite(code) {
    if (this.data.mySchedule.includes(code)) {
      // Remove from favorites
      this.data.mySchedule = this.data.mySchedule.filter(c => c !== code);
    } else {
      // Add to favorites
      this.data.mySchedule.push(code);
    }

    // Save to localStorage
    this.saveMySchedule();

    // Update UI - toggle active class on button in list view
    const btn = document.querySelector(`.session-favorite[data-code="${code}"]`);
    if (btn) {
      btn.classList.toggle('active');
    }

    // Update is-favorite class on card in list view
    const card = document.querySelector(`.session-card[data-session-code="${code}"]`);
    if (card) {
      card.classList.toggle('is-favorite');
    }
  },

  // ============================================
  // TASK 11: SESSION DETAIL PANEL
  // ============================================

  // Open session detail panel
  openSessionDetail(code) {
    const session = this.data.sessions.find(s => s.code === code);
    if (!session) {
      console.error('Session not found:', code);
      return;
    }

    // Create panel if doesn't exist
    if (!document.querySelector('.detail-overlay')) {
      this.createDetailPanel();
    }

    // Populate panel with session data
    this.populateDetailPanel(session);

    // Show panel with animation
    this.showDetailPanel();

    // Update URL to include session parameter
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('session', code);
    const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
    history.pushState({ scheduleState: this.state }, '', newUrl);
  },

  // Create detail panel HTML structure
  createDetailPanel() {
    const overlay = document.createElement('div');
    overlay.className = 'detail-overlay';
    overlay.setAttribute('aria-hidden', 'true');

    const panel = document.createElement('aside');
    panel.className = 'detail-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'detail-panel-title');

    panel.innerHTML = `
      <div class="detail-header">
        <button class="detail-close" aria-label="Close panel" type="button">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="detail-content">
        <div class="detail-session-header">
          <h2 id="detail-panel-title" class="detail-code"></h2>
          <div class="detail-sport">
            <span class="sport-icon"></span>
            <span class="sport-name"></span>
          </div>
        </div>

        <div class="detail-section">
          <h3>Event</h3>
          <p class="detail-event"></p>
        </div>

        <div class="detail-section">
          <h3>Date & Time</h3>
          <p class="detail-date"></p>
          <p class="detail-time"></p>
        </div>

        <div class="detail-section">
          <h3>Venue</h3>
          <p class="detail-venue">
            <span class="venue-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </span>
            <span class="venue-name"></span>
          </p>
        </div>

        <div class="detail-actions">
          <a href="#" class="detail-action-btn detail-buy-btn" target="_blank">
            <span>Buy Tickets</span>
          </a>
          <button class="detail-action-btn detail-calendar-btn" type="button">
            <span>Add to Calendar</span>
          </button>
        </div>
      </div>
    `;

    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    // Attach event listeners
    this.attachDetailPanelListeners();
  },

  // Attach event listeners to detail panel
  attachDetailPanelListeners() {
    const overlay = document.querySelector('.detail-overlay');
    const closeBtn = document.querySelector('.detail-close');
    const favoriteBtn = document.querySelector('.detail-favorite-btn');
    const calendarBtn = document.querySelector('.detail-calendar-btn');

    // Close button
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeSessionDetail());
    }

    // Backdrop click
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeSessionDetail();
        }
      });
    }

    // ESC key handler
    this.escKeyHandler = (e) => {
      if (e.key === 'Escape') {
        this.closeSessionDetail();
      }
    };
    document.addEventListener('keydown', this.escKeyHandler);

    // Favorite button (event delegation for dynamic content)
    if (favoriteBtn) {
      favoriteBtn.addEventListener('click', () => {
        const code = favoriteBtn.dataset.code;
        if (code) {
          this.toggleFavorite(code);
          // Update button state
          const session = this.data.sessions.find(s => s.code === code);
          if (session) {
            this.populateDetailPanel(session);
          }
        }
      });
    }

    // Calendar button
    if (calendarBtn) {
      calendarBtn.addEventListener('click', () => {
        const code = calendarBtn.dataset.code;
        if (code) {
          const session = this.data.sessions.find(s => s.code === code);
          if (session) {
            this.downloadCalendarFile(session);
          }
        }
      });
    }
  },

  // Populate detail panel with session data
  populateDetailPanel(session) {
    const isFavorite = this.data.mySchedule.includes(session.code);
    const sportColor = this.data.sportColors[session.sport] || '#999';
    const sportIcon = this.getSportIcon(session.sport);

    // Format date
    const date = new Date(session.date + 'T00:00:00');
    const dateStr = date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Parse time
    const timeStr = session.time;
    const [startTime, endTime] = timeStr.includes('-') ? timeStr.split('-').map(t => t.trim()) : [timeStr, ''];
    const duration = endTime ? this.calculateDuration(startTime, endTime) : '';

    // Update panel content
    const panel = document.querySelector('.detail-panel');
    if (!panel) return;

    const codeEl = panel.querySelector('.detail-code');
    if (codeEl) {
      codeEl.textContent = session.code;
      codeEl.style.color = sportColor;
    }

    const sportIconEl = panel.querySelector('.sport-icon');
    if (sportIconEl) sportIconEl.innerHTML = sportIcon;

    const sportNameEl = panel.querySelector('.sport-name');
    if (sportNameEl) sportNameEl.textContent = session.sport;

    const eventEl = panel.querySelector('.detail-event');
    if (eventEl) eventEl.textContent = session.event;

    const dateEl = panel.querySelector('.detail-date');
    if (dateEl) dateEl.textContent = dateStr;

    const timeEl = panel.querySelector('.detail-time');
    if (timeEl) {
      if (duration) {
        timeEl.textContent = `${startTime} - ${endTime} (${duration})`;
      } else {
        timeEl.textContent = timeStr;
      }
    }

    const venueNameEl = panel.querySelector('.venue-name');
    if (venueNameEl) venueNameEl.textContent = session.venue;

    // Update favorite button
    const favoriteBtn = panel.querySelector('.detail-favorite-btn');
    if (favoriteBtn) {
      favoriteBtn.dataset.code = session.code;
      favoriteBtn.classList.toggle('active', isFavorite);

      const favoriteIcon = favoriteBtn.querySelector('.favorite-icon');
      if (favoriteIcon) {
        favoriteIcon.innerHTML = isFavorite ? '★' : '☆';
      }

      const favoriteText = favoriteBtn.querySelector('.favorite-text');
      if (favoriteText) {
        favoriteText.textContent = isFavorite ? 'Remove from My Schedule' : 'Add to My Schedule';
      }
    }

    // Update calendar button
    const calendarBtn = panel.querySelector('.detail-calendar-btn');
    if (calendarBtn) {
      calendarBtn.dataset.code = session.code;
    }

    // Update buy tickets button
    const buyBtn = panel.querySelector('.detail-buy-btn');
    if (buyBtn) {
      // Use productUrl from session data (already includes correct handle)
      buyBtn.href = session.productUrl || `/products/la-2028-summer-games-${session.code.toLowerCase()}`;
    }
  },

  // Show detail panel with animation
  showDetailPanel() {
    const overlay = document.querySelector('.detail-overlay');
    if (!overlay) return;

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Trigger animation
    requestAnimationFrame(() => {
      overlay.classList.add('is-active');
      const panel = overlay.querySelector('.detail-panel');
      if (panel) {
        panel.classList.add('is-active');
        // Focus close button for accessibility
        const closeBtn = panel.querySelector('.detail-close');
        if (closeBtn) closeBtn.focus();
      }
    });
  },

  // Close session detail panel
  closeSessionDetail() {
    const overlay = document.querySelector('.detail-overlay');
    if (!overlay) return;

    const panel = overlay.querySelector('.detail-panel');
    if (panel) {
      panel.classList.remove('is-active');
    }
    overlay.classList.remove('is-active');

    // Restore body scroll after animation
    setTimeout(() => {
      document.body.style.overflow = '';
    }, 300);

    // Remove ESC key listener
    if (this.escKeyHandler) {
      document.removeEventListener('keydown', this.escKeyHandler);
      this.escKeyHandler = null;
    }

    // Remove session parameter from URL
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.delete('session');
    const newUrl = currentParams.toString()
      ? `${window.location.pathname}?${currentParams.toString()}`
      : window.location.pathname;
    history.replaceState({ scheduleState: this.state }, '', newUrl);
  },

  // Calculate duration between times
  calculateDuration(start, end) {
    try {
      const [startH, startM] = start.split(':').map(Number);
      const [endH, endM] = end.split(':').map(Number);

      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      const diffMinutes = endMinutes - startMinutes;

      if (diffMinutes <= 0) return '';

      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;

      if (hours === 0) return `${minutes}m`;
      if (minutes === 0) return `${hours}h`;
      return `${hours}h ${minutes}m`;
    } catch (error) {
      console.error('Error calculating duration:', error);
      return '';
    }
  },

  // Download calendar file (.ics)
  downloadCalendarFile(session) {
    try {
      // Format date and time for iCalendar format (YYYYMMDDTHHMMSS)
      const dateStr = session.date.replace(/-/g, '');
      const [startTime, endTime] = session.time.includes('-')
        ? session.time.split('-').map(t => t.trim())
        : [session.time, session.time];

      // Parse start time
      const [startH, startM] = startTime.split(':').map(Number);
      const startTimeStr = `${String(startH).padStart(2, '0')}${String(startM).padStart(2, '0')}00`;

      // Parse end time (or default to 2 hours after start)
      let endTimeStr;
      if (endTime && endTime !== startTime) {
        const [endH, endM] = endTime.split(':').map(Number);
        endTimeStr = `${String(endH).padStart(2, '0')}${String(endM).padStart(2, '0')}00`;
      } else {
        const endH = (startH + 2) % 24;
        endTimeStr = `${String(endH).padStart(2, '0')}${String(startM).padStart(2, '0')}00`;
      }

      // Current timestamp for DTSTAMP
      const now = new Date();
      const dtstamp = now.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

      // Build product URL (use productUrl from session data if available)
      const productUrl = session.productUrl
        ? `https://goaltickets.com${session.productUrl}`
        : `https://goaltickets.com/products/la-2028-summer-games-${session.code.toLowerCase()}`;

      // Build iCalendar content
      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//LA 2028 Olympics//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `UID:${session.code}@la2028.com`,
        `DTSTAMP:${dtstamp}`,
        `DTSTART:${dateStr}T${startTimeStr}`,
        `DTEND:${dateStr}T${endTimeStr}`,
        `SUMMARY:${session.sport} - ${session.event}`,
        `LOCATION:${session.venue}`,
        `DESCRIPTION:LA 2028 Olympics Session ${session.code}\\n\\nBuy tickets at: ${productUrl}`,
        `URL:${productUrl}`,
        'STATUS:CONFIRMED',
        'SEQUENCE:0',
        'END:VEVENT',
        'END:VCALENDAR'
      ].join('\r\n');

      // Create blob and download
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `LA2028-${session.code}.ics`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Error creating calendar file:', error);
      alert('Failed to create calendar file. Please try again.');
    }
  },

  // Load more sessions (pagination)
  loadMoreSessions() {
    const content = document.querySelector('.session-grid');
    if (!content) return;

    const currentCount = content.querySelectorAll('.session-card').length;
    const nextBatch = this.data.filteredSessions
      .slice(currentCount, currentCount + 50)
      .map(session => this.createSessionCard(session))
      .join('');

    // Add new cards to grid
    content.insertAdjacentHTML('beforeend', nextBatch);

    // Update load more button
    const loadMoreBtn = document.getElementById('load-more-btn');
    const remaining = this.data.filteredSessions.length - currentCount - 50;

    if (remaining <= 0) {
      // Remove button if all sessions loaded
      loadMoreBtn.remove();
    } else {
      // Update button text with remaining count
      loadMoreBtn.textContent = `Load More Sessions (${remaining} remaining)`;
    }

    // Re-attach listeners for new cards
    this.attachSessionCardListeners();
  },

  // ============================================
  // TASK 12: CALENDAR VIEW
  // ============================================

  // Render calendar view
  renderCalendarView() {
    const content = document.getElementById('schedule-content');
    if (!content) return;

    // Initialize week to first day of Olympics if not set
    if (!this.state.currentWeekStart) {
      const firstSession = this.data.filteredSessions.length > 0
        ? this.data.filteredSessions[0]
        : this.data.sessions[0];

      if (firstSession) {
        this.state.currentWeekStart = this.getWeekStart(new Date(firstSession.date + 'T00:00:00'));
      } else {
        // Fallback to July 14, 2028 (Opening Ceremony)
        this.state.currentWeekStart = new Date(2028, 6, 10); // Monday, July 10, 2028
      }
    }

    // Render calendar structure
    content.innerHTML = `
      <div class="calendar-view">
        <div class="calendar-header">
          <div class="calendar-nav">
            <button class="calendar-nav-btn" id="prev-week">&larr; Previous Week</button>
            <button class="calendar-nav-btn calendar-today" id="today-btn">Today</button>
            <button class="calendar-nav-btn" id="next-week">Next Week &rarr;</button>
          </div>
          <div class="calendar-week-range"></div>
        </div>
        ${this.renderCalendarMobileDayNav()}
        <div class="calendar-grid-container">
          <div class="calendar-grid"></div>
        </div>
      </div>
    `;

    // Render week navigation and grid
    this.renderWeekNavigation();
    this.renderCalendarGrid();
    this.renderCalendarSessions();
    this.attachCalendarListeners();

    // Attach mobile day navigation if on mobile
    if (window.innerWidth <= 768) {
      this.attachMobileDayNavListeners();
      this.updateMobileDayDisplay();
    }
  },

  // Get Monday of the week for a given date
  getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Adjust for Monday start
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  },

  // Get array of 7 dates for the week
  getWeekDates(startDate) {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    return dates;
  },

  // Render week navigation controls
  renderWeekNavigation() {
    const rangeEl = document.querySelector('.calendar-week-range');
    if (!rangeEl) return;

    const weekDates = this.getWeekDates(this.state.currentWeekStart);
    const startStr = weekDates[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const endStr = weekDates[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    rangeEl.textContent = `${startStr} - ${endStr}`;

    // Enable/disable navigation buttons based on date range
    const prevBtn = document.getElementById('prev-week');
    const nextBtn = document.getElementById('next-week');

    // Olympics dates: July 14 - July 30, 2028
    const olympicsStart = new Date(2028, 6, 14); // July 14, 2028
    const olympicsEnd = new Date(2028, 6, 30); // July 30, 2028

    if (prevBtn) {
      const prevWeek = new Date(this.state.currentWeekStart);
      prevWeek.setDate(prevWeek.getDate() - 7);
      prevBtn.disabled = prevWeek < new Date(2028, 6, 1); // Before July 1, 2028
    }

    if (nextBtn) {
      const nextWeek = new Date(this.state.currentWeekStart);
      nextWeek.setDate(nextWeek.getDate() + 7);
      nextBtn.disabled = nextWeek > new Date(2028, 7, 15); // After August 15, 2028
    }
  },

  // Render calendar grid structure
  renderCalendarGrid() {
    const grid = document.querySelector('.calendar-grid');
    if (!grid) return;

    const weekDates = this.getWeekDates(this.state.currentWeekStart);

    // Time slots from 8 AM to 11 PM (15 hours)
    const timeSlots = [];
    for (let hour = 8; hour <= 23; hour++) {
      timeSlots.push(hour);
    }

    let html = '';

    // Header row - empty cell for time column + day headers
    html += '<div class="calendar-time-header"></div>';
    weekDates.forEach(date => {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const isToday = this.isToday(date);

      html += `
        <div class="calendar-day-header ${isToday ? 'is-today' : ''}">
          <div class="calendar-day-name">${dayName}</div>
          <div class="calendar-day-date">${dateStr}</div>
        </div>
      `;
    });

    // Time slots and cells
    timeSlots.forEach(hour => {
      // Time label
      const timeLabel = this.formatHour(hour);
      html += `<div class="calendar-time-slot">${timeLabel}</div>`;

      // Cells for each day
      weekDates.forEach(date => {
        const dateStr = this.formatDateString(date);
        const cellId = `cell-${dateStr}-${hour}`;
        const isToday = this.isToday(date);

        html += `
          <div class="calendar-cell ${isToday ? 'is-today' : ''}"
               data-date="${dateStr}"
               data-hour="${hour}"
               id="${cellId}">
          </div>
        `;
      });
    });

    grid.innerHTML = html;
  },

  // Render sessions in calendar grid
  renderCalendarSessions() {
    const weekDates = this.getWeekDates(this.state.currentWeekStart);
    const weekDateStrings = weekDates.map(d => this.formatDateString(d));

    // Filter sessions for current week
    const weekSessions = this.data.filteredSessions.filter(session =>
      weekDateStrings.includes(session.date)
    );

    // Group sessions by date and time slot
    const sessionsByCell = {};

    weekSessions.forEach(session => {
      const hour = this.getSessionHour(session.time);
      if (hour === null) return; // Skip sessions without valid time

      const cellId = `cell-${session.date}-${hour}`;

      if (!sessionsByCell[cellId]) {
        sessionsByCell[cellId] = [];
      }
      sessionsByCell[cellId].push(session);
    });

    // Render sessions in each cell
    Object.entries(sessionsByCell).forEach(([cellId, sessions]) => {
      const cell = document.getElementById(cellId);
      if (!cell) return;

      const maxVisible = 3; // Show max 3 sessions per cell
      const visibleSessions = sessions.slice(0, maxVisible);
      const remainingCount = sessions.length - maxVisible;

      let html = '';
      visibleSessions.forEach(session => {
        html += this.createCalendarSessionCard(session);
      });

      if (remainingCount > 0) {
        html += `<div class="session-overflow">+${remainingCount} more</div>`;
      }

      cell.innerHTML = html;
    });

    // Attach click listeners to calendar session cards
    document.querySelectorAll('.calendar-session').forEach(card => {
      card.addEventListener('click', (e) => {
        e.stopPropagation();
        const code = card.dataset.code;
        this.openSessionDetail(code);
      });
    });
  },

  // Create mini session card for calendar
  createCalendarSessionCard(session) {
    const isFavorite = this.data.mySchedule.includes(session.code);
    const sportColor = this.data.sportColors[session.sport] || '#999';
    const sportIcon = this.getSportIcon(session.sport);

    // Truncate event name
    const maxLength = 30;
    const eventName = session.event.length > maxLength
      ? session.event.substring(0, maxLength) + '...'
      : session.event;

    return `
      <div class="calendar-session ${isFavorite ? 'is-favorite' : ''}"
           data-code="${session.code}"
           data-event="${session.event}"
           style="--sport-color: ${sportColor}; --sport-color-dark: ${this.darkenColor(sportColor)}"
           title="${session.event} - ${session.time}">
        <div class="calendar-sport-name">
          ${session.sport}
          ${isFavorite ? '<span class="calendar-favorite-star">★</span>' : ''}
        </div>
        <div class="calendar-session-name">${eventName}</div>
        <div class="calendar-session-code">${session.code}</div>
      </div>
    `;
  },

  // Attach calendar-specific event listeners
  attachCalendarListeners() {
    const prevBtn = document.getElementById('prev-week');
    const nextBtn = document.getElementById('next-week');
    const todayBtn = document.getElementById('today-btn');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.navigateWeek(-1));
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.navigateWeek(1));
    }

    if (todayBtn) {
      todayBtn.addEventListener('click', () => this.jumpToToday());
    }
  },

  // Navigate to previous/next week
  navigateWeek(direction) {
    const newWeekStart = new Date(this.state.currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + (direction * 7));
    this.state.currentWeekStart = newWeekStart;

    // Re-render calendar
    this.renderWeekNavigation();
    this.renderCalendarGrid();
    this.renderCalendarSessions();

    // Update URL to reflect week change
    this.updateUrl();
  },

  // Jump to week containing today
  jumpToToday() {
    const today = new Date();
    this.state.currentWeekStart = this.getWeekStart(today);

    // Re-render calendar
    this.renderWeekNavigation();
    this.renderCalendarGrid();
    this.renderCalendarSessions();

    // Update URL to reflect week change
    this.updateUrl();
  },

  // Helper: Check if date is today
  isToday(date) {
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
           date.getMonth() === today.getMonth() &&
           date.getDate() === today.getDate();
  },

  // Helper: Format date as YYYY-MM-DD
  formatDateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  },

  // Helper: Format hour as time string
  formatHour(hour) {
    if (hour === 0) return '12:00 AM';
    if (hour < 12) return `${hour}:00 AM`;
    if (hour === 12) return '12:00 PM';
    return `${hour - 12}:00 PM`;
  },

  // Helper: Extract hour from session time
  getSessionHour(timeStr) {
    if (!timeStr) return null;

    // Extract start time from "HH:MM" or "HH:MM - HH:MM" format
    const startTime = timeStr.split('-')[0].trim();
    const timeParts = startTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);

    if (!timeParts) return null;

    let hour = parseInt(timeParts[1]);
    const period = timeParts[3];

    // Convert to 24-hour format
    if (period) {
      if (period.toUpperCase() === 'PM' && hour !== 12) {
        hour += 12;
      } else if (period.toUpperCase() === 'AM' && hour === 12) {
        hour = 0;
      }
    }

    return hour;
  },

  // Helper: Darken a color for borders
  darkenColor(color) {
    // Simple darkening by reducing each RGB component
    if (!color || !color.startsWith('#')) return '#666';

    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const darken = (c) => Math.max(0, Math.floor(c * 0.7));

    const newR = darken(r).toString(16).padStart(2, '0');
    const newG = darken(g).toString(16).padStart(2, '0');
    const newB = darken(b).toString(16).padStart(2, '0');

    return `#${newR}${newG}${newB}`;
  },

  // ============================================
  // TASK 13: VENUES VIEW
  // ============================================

  // Render venues view
  renderVenuesView() {
    const content = document.getElementById('schedule-content');
    if (!content) return;

    // Get venues with filtered session counts
    const venuesWithSessions = this.getVenuesWithFilteredSessions();

    if (venuesWithSessions.length === 0) {
      content.innerHTML = `
        <div class="schedule-empty">
          <p>No venues found matching your filters</p>
          <button onclick="ScheduleApp.clearAllFilters()">Clear Filters</button>
        </div>
      `;
      return;
    }

    // Sort venues
    this.sortVenues(venuesWithSessions);

    // Render venues view
    content.innerHTML = `
      <div class="venues-view">
        <div class="venues-header">
          <h2>Venues (${venuesWithSessions.length})</h2>
          <select id="venues-sort" class="venues-sort">
            <option value="sessions" ${this.state.venuesSortBy === 'sessions' ? 'selected' : ''}>Sort by Sessions</option>
            <option value="name" ${this.state.venuesSortBy === 'name' ? 'selected' : ''}>Sort by Name</option>
          </select>
        </div>
        <div class="venues-list">
          ${venuesWithSessions.map(venue => this.renderVenueCard(venue)).join('')}
        </div>
      </div>
    `;

    // Attach event listeners
    this.attachVenuesListeners();
  },

  // Get venues with filtered sessions
  getVenuesWithFilteredSessions() {
    // For each venue, get filtered sessions
    const venueMap = {};

    this.data.filteredSessions.forEach(session => {
      if (!venueMap[session.venue]) {
        // Find venue data from venues array
        const venueData = this.data.venues.find(v => v.name === session.venue) || {
          name: session.venue,
          sports: []
        };

        venueMap[session.venue] = {
          ...venueData,
          sessions: []
        };
      }
      venueMap[session.venue].sessions.push(session);
    });

    // Convert to array
    return Object.values(venueMap);
  },

  // Sort venues
  sortVenues(venues) {
    if (this.state.venuesSortBy === 'sessions') {
      venues.sort((a, b) => b.sessions.length - a.sessions.length);
    } else {
      venues.sort((a, b) => a.name.localeCompare(b.name));
    }
  },

  // Render venue card
  renderVenueCard(venue) {
    const isExpanded = this.state.expandedVenues.has(venue.name);

    // Get unique sports from sessions
    const sportsSet = new Set(venue.sessions.map(s => s.sport));
    const sports = Array.from(sportsSet).sort();

    const sportsList = sports.map(sport => `
      <span class="venue-sport-badge">
        ${this.getSportIcon(sport)} ${sport}
      </span>
    `).join('');

    return `
      <div class="venue-card ${isExpanded ? 'is-expanded' : ''}" data-venue="${venue.name}">
        <div class="venue-header">
          <h3 class="venue-name">
            <span class="venue-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </span>
            ${venue.name}
          </h3>
          <div class="venue-stats">
            <span class="venue-session-count">${venue.sessions.length} Session${venue.sessions.length !== 1 ? 's' : ''}</span>
            <div class="venue-sports">${sportsList}</div>
          </div>
          <button class="venue-toggle-btn" data-venue="${venue.name}" aria-label="${isExpanded ? 'Hide' : 'View'} sessions for ${venue.name}">
            ${isExpanded ? 'Hide Sessions' : 'View Sessions'}
          </button>
        </div>
        <div class="venue-sessions ${isExpanded ? 'is-visible' : ''}">
          ${isExpanded ? this.renderVenueSessions(venue.sessions) : ''}
        </div>
      </div>
    `;
  },

  // Render venue sessions grouped by date
  renderVenueSessions(sessions) {
    // Group by date
    const grouped = {};
    sessions.forEach(session => {
      if (!grouped[session.date]) grouped[session.date] = [];
      grouped[session.date].push(session);
    });

    // Sort dates
    const dates = Object.keys(grouped).sort();

    return dates.map(date => {
      const dateSessions = grouped[date].sort((a, b) =>
        a.time.localeCompare(b.time)
      );

      const dateObj = new Date(date + 'T00:00:00');
      const dateStr = dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });

      return `
        <div class="venue-date-group">
          <h4 class="venue-date-header">${dateStr}</h4>
          <div class="venue-session-list">
            ${dateSessions.map(s => this.createVenueSessionItem(s)).join('')}
          </div>
        </div>
      `;
    }).join('');
  },

  // Create venue session item
  createVenueSessionItem(session) {
    const icon = this.getSportIcon(session.sport);
    const sportColor = this.data.sportColors[session.sport] || '#999';
    const isFavorite = this.data.mySchedule.includes(session.code);

    return `
      <div class="venue-session-item" data-session-code="${session.code}">
        <span class="session-time">${session.time}</span>
        <span class="session-sport" style="color: ${sportColor}">
          ${icon} ${session.sport}
        </span>
        <span class="session-event">${session.event}</span>
        <button class="session-favorite ${isFavorite ? 'active' : ''}"
                data-code="${session.code}"
                aria-label="${isFavorite ? 'Remove from' : 'Add to'} My Schedule">
          <span class="favorite-icon"></span>
        </button>
      </div>
    `;
  },

  // Toggle venue expansion (OPTIMIZED - no full re-render)
  toggleVenueExpansion(venueName) {
    const isExpanding = !this.state.expandedVenues.has(venueName);

    if (isExpanding) {
      this.state.expandedVenues.add(venueName);
    } else {
      this.state.expandedVenues.delete(venueName);
    }

    // Find the specific venue card and toggle it (avoid full re-render)
    const venueCard = document.querySelector(`.venue-card[data-venue="${venueName}"]`);
    if (!venueCard) return;

    const sessionsContainer = venueCard.querySelector('.venue-sessions');
    const toggleBtn = venueCard.querySelector('.venue-toggle-btn');

    if (isExpanding) {
      // Expanding - render sessions and show
      venueCard.classList.add('is-expanded');
      toggleBtn.textContent = 'Hide Sessions';
      toggleBtn.setAttribute('aria-label', `Hide sessions for ${venueName}`);

      // Get venue data and render sessions
      const venueData = this.getVenuesWithFilteredSessions().find(v => v.name === venueName);
      if (venueData) {
        sessionsContainer.innerHTML = this.renderVenueSessions(venueData.sessions);
        sessionsContainer.classList.add('is-visible');

        // Attach event listeners to new session items
        this.attachVenueSessionListeners(venueCard);
      }
    } else {
      // Collapsing - hide
      venueCard.classList.remove('is-expanded');
      toggleBtn.textContent = 'View Sessions';
      toggleBtn.setAttribute('aria-label', `View sessions for ${venueName}`);
      sessionsContainer.classList.remove('is-visible');

      // Clear content after animation
      setTimeout(() => {
        if (!sessionsContainer.classList.contains('is-visible')) {
          sessionsContainer.innerHTML = '';
        }
      }, 400);
    }

    // Update URL to reflect expanded venues
    this.updateUrl();
  },

  // Attach event listeners to venue session items (for single venue)
  attachVenueSessionListeners(venueCard) {
    // Session items - click to view details
    venueCard.querySelectorAll('.venue-session-item').forEach(item => {
      item.addEventListener('click', (e) => {
        // Don't trigger if clicking favorite button
        if (e.target.closest('.session-favorite')) return;

        const code = item.dataset.sessionCode;
        this.openSessionDetail(code);
      });
    });

    // Favorite buttons within venue sessions
    venueCard.querySelectorAll('.venue-session-item .session-favorite').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const code = btn.dataset.code;
        this.toggleFavorite(code);
        // Update the button state immediately
        btn.classList.toggle('active');
      });
    });
  },

  // Attach venues view event listeners
  attachVenuesListeners() {
    // Sort dropdown
    const sortSelect = document.getElementById('venues-sort');
    if (sortSelect) {
      sortSelect.addEventListener('change', (e) => {
        this.state.venuesSortBy = e.target.value;
        this.renderVenuesView();

        // Update URL to reflect sort change
        this.updateUrl();
      });
    }

    // Venue toggle buttons
    document.querySelectorAll('.venue-toggle-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const venueName = btn.dataset.venue;
        this.toggleVenueExpansion(venueName);
      });
    });

    // Session items - click to view details
    document.querySelectorAll('.venue-session-item').forEach(item => {
      item.addEventListener('click', (e) => {
        // Don't trigger if clicking favorite button
        if (e.target.closest('.session-favorite')) return;

        const code = item.dataset.sessionCode;
        this.openSessionDetail(code);
      });
    });

    // Favorite buttons within venue sessions
    document.querySelectorAll('.venue-session-item .session-favorite').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const code = btn.dataset.code;
        this.toggleFavorite(code);
        // Update the button state immediately
        btn.classList.toggle('active');
      });
    });
  },

  // ============================================
  // TASK 15: MOBILE RESPONSIVE FEATURES
  // ============================================

  // Toggle mobile filter drawer
  toggleMobileFilters() {
    const filterBar = document.querySelector('.schedule-filter-bar');
    const backdrop = document.querySelector('.filter-drawer-backdrop');
    const filterBtn = document.querySelector('.mobile-filters-btn');

    if (filterBar && backdrop) {
      const isOpen = filterBar.classList.contains('is-open');

      if (isOpen) {
        // Close drawer
        filterBar.classList.remove('is-open');
        backdrop.classList.remove('is-visible');
        document.body.style.overflow = '';

        // Update ARIA attributes
        if (filterBtn) {
          filterBtn.setAttribute('aria-expanded', 'false');
        }
      } else {
        // Open drawer
        filterBar.classList.add('is-open');
        backdrop.classList.add('is-visible');
        document.body.style.overflow = 'hidden';

        // Update ARIA attributes
        if (filterBtn) {
          filterBtn.setAttribute('aria-expanded', 'true');
        }

        // Focus first interactive element in drawer
        const firstInput = filterBar.querySelector('input, select, button');
        if (firstInput) {
          setTimeout(() => firstInput.focus(), 100);
        }
      }
    }
  },

  // Update mobile filter badge count
  updateMobileFilterBadge() {
    const badge = document.querySelector('.filter-count-badge');
    if (!badge) return;

    const activeCount =
      (this.state.filters.dateRange ? 1 : 0) +
      this.state.filters.sports.length +
      this.state.filters.venues.length +
      (this.state.filters.searchQuery ? 1 : 0);

    if (activeCount > 0) {
      badge.textContent = activeCount;
      badge.style.display = 'flex';
    } else {
      badge.style.display = 'none';
    }
  },

  // Render mobile calendar day navigation
  renderCalendarMobileDayNav() {
    if (window.innerWidth > 768) return '';

    const weekDates = this.getWeekDates(this.state.currentWeekStart);
    const currentDate = weekDates[this.state.currentMobileDayIndex];
    const dateStr = currentDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });

    const isPrevDisabled = this.state.currentMobileDayIndex === 0;
    const isNextDisabled = this.state.currentMobileDayIndex === 6;
    const isToday = this.isToday(currentDate);

    return `
      <div class="calendar-mobile-day-nav">
        <button id="mobile-prev-day" ${isPrevDisabled ? 'disabled' : ''} aria-label="Previous day">
          &larr;
        </button>
        <div class="calendar-mobile-current-day">
          ${dateStr}
          ${isToday ? '<br><span style="font-size: 12px; color: #FFD700;">Today</span>' : ''}
        </div>
        <button id="mobile-next-day" ${isNextDisabled ? 'disabled' : ''} aria-label="Next day">
          &rarr;
        </button>
      </div>
    `;
  },

  // Navigate mobile calendar day
  navigateMobileDay(direction) {
    const newIndex = this.state.currentMobileDayIndex + direction;

    // Boundary check
    if (newIndex < 0 || newIndex > 6) return;

    this.state.currentMobileDayIndex = newIndex;

    // Update calendar view to show the selected day
    this.updateMobileDayDisplay();
  },

  // Update mobile day display (CSS-based column visibility) - PERFORMANCE OPTIMIZED
  updateMobileDayDisplay() {
    const grid = document.querySelector('.calendar-grid');
    if (!grid || window.innerWidth > 768) return;

    // Collect all DOM updates first (avoid layout thrashing)
    const updates = [];
    const allDayHeaders = grid.querySelectorAll('.calendar-day-header');
    const allCells = grid.querySelectorAll('.calendar-cell');

    // Collect header updates
    allDayHeaders.forEach((header, index) => {
      updates.push({
        element: header,
        display: index === this.state.currentMobileDayIndex ? 'flex' : 'none'
      });
    });

    // Collect cell updates
    allCells.forEach((cell, index) => {
      const cellDayColumn = (index % 7);
      updates.push({
        element: cell,
        display: cellDayColumn === this.state.currentMobileDayIndex ? 'flex' : 'none'
      });
    });

    // Apply all updates in a single animation frame (batch update)
    requestAnimationFrame(() => {
      updates.forEach(({ element, display }) => {
        element.style.display = display;
      });
    });

    // Re-render mobile day nav to update state
    const existingNav = document.querySelector('.calendar-mobile-day-nav');
    if (existingNav) {
      existingNav.outerHTML = this.renderCalendarMobileDayNav();
      this.attachMobileDayNavListeners();
    }
  },

  // Attach mobile day navigation listeners
  attachMobileDayNavListeners() {
    const prevBtn = document.getElementById('mobile-prev-day');
    const nextBtn = document.getElementById('mobile-next-day');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.navigateMobileDay(-1));
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.navigateMobileDay(1));
    }
  },

  // Attach mobile-specific event listeners - WITH CLEANUP
  attachMobileListeners() {
    // Remove old listeners first to prevent memory leaks
    this.removeMobileListeners();

    // Store listener references for cleanup
    this.mobileListeners = {
      filterBtn: () => this.toggleMobileFilters(),
      backdrop: () => this.toggleMobileFilters(),
      closeBtn: () => this.toggleMobileFilters(),
      applyBtn: () => this.toggleMobileFilters(),
      escKey: (e) => {
        if (e.key === 'Escape') {
          const filterBar = document.querySelector('.schedule-filter-bar');
          if (filterBar && filterBar.classList.contains('is-open')) {
            this.toggleMobileFilters();
          }
        }
      }
    };

    // Mobile filter button
    const filtersBtn = document.querySelector('.mobile-filters-btn');
    if (filtersBtn) {
      filtersBtn.addEventListener('click', this.mobileListeners.filterBtn);
    }

    // Filter drawer backdrop
    const backdrop = document.querySelector('.filter-drawer-backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', this.mobileListeners.backdrop);
    }

    // Filter drawer close button
    const closeBtn = document.querySelector('.filter-drawer-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.mobileListeners.closeBtn);
    }

    // Filter apply button (closes drawer)
    const applyBtn = document.querySelector('.filter-apply-btn');
    if (applyBtn) {
      applyBtn.addEventListener('click', this.mobileListeners.applyBtn);
    }

    // Close drawer on ESC key
    document.addEventListener('keydown', this.mobileListeners.escKey);

    // Update badge when filters change
    this.updateMobileFilterBadge();
  },

  // Remove mobile listeners to prevent memory leaks
  removeMobileListeners() {
    if (!this.mobileListeners) return;

    const filtersBtn = document.querySelector('.mobile-filters-btn');
    const backdrop = document.querySelector('.filter-drawer-backdrop');
    const closeBtn = document.querySelector('.filter-drawer-close');
    const applyBtn = document.querySelector('.filter-apply-btn');

    if (filtersBtn) filtersBtn.removeEventListener('click', this.mobileListeners.filterBtn);
    if (backdrop) backdrop.removeEventListener('click', this.mobileListeners.backdrop);
    if (closeBtn) closeBtn.removeEventListener('click', this.mobileListeners.closeBtn);
    if (applyBtn) applyBtn.removeEventListener('click', this.mobileListeners.applyBtn);
    document.removeEventListener('keydown', this.mobileListeners.escKey);

    this.mobileListeners = null;
  },

  // Initialize mobile features
  initMobileFeatures() {
    // Only run on mobile
    if (window.innerWidth <= 768) {
      this.attachMobileListeners();

      // If on calendar view, initialize mobile day navigation
      if (this.state.activeView === 'calendar') {
        this.state.currentMobileDayIndex = 0; // Start with Monday
      }
    }

    // Handle window resize (passive for performance)
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const isMobile = window.innerWidth <= 768;

        if (isMobile) {
          this.attachMobileListeners();
          this.updateMobileFilterBadge();
        } else {
          // Clean up mobile-only features on desktop
          this.removeMobileListeners();
          const filterBar = document.querySelector('.schedule-filter-bar');
          const backdrop = document.querySelector('.filter-drawer-backdrop');

          if (filterBar) filterBar.classList.remove('is-open');
          if (backdrop) backdrop.classList.remove('is-visible');
          document.body.style.overflow = '';
        }
      }, 250);
    }, { passive: true });
  },

  // Initialize swipe gestures for filter drawer (mobile)
  initSwipeGestures() {
    if (window.innerWidth > 768) return;

    const drawer = document.querySelector('.schedule-filter-bar');
    if (!drawer) return;

    let startY = 0, currentY = 0, isDragging = false;

    drawer.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
      isDragging = true;
    }, { passive: true });

    drawer.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentY = e.touches[0].clientY;
      const diff = currentY - startY;

      // Only allow downward swipes
      if (diff > 0) {
        drawer.style.transform = `translateY(${diff}px)`;
        drawer.style.transition = 'none';
      }
    }, { passive: true });

    drawer.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;

      const diff = currentY - startY;

      // If swiped down more than 100px, close drawer
      if (diff > 100) {
        this.toggleMobileFilters();
      }

      // Reset transform
      drawer.style.transform = '';
      drawer.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    }, { passive: true });
  },
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ScheduleApp.init());
} else {
  ScheduleApp.init();
}
